"use client";

import DynamicImage from "@/components/common/DynamicImage";
import { ChevronRight, Menu, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { HOME_PAGE } from "routes";
import ExitPopup from "./ExitPopup";
import Navbar from "./Navbar";

const COUPON_CODE = "TRY26";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;

    let popupShown = false;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !popupShown) {
        popupShown = true;
        setShowExitPopup(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  useEffect(() => {
    if (!showExitPopup) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowExitPopup(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showExitPopup]);

  const toggleMenu = () => setIsMenuOpen((open) => !open);
  const closePopup = () => setShowExitPopup(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      {showExitPopup && (
        <ExitPopup
          closePopup={closePopup}
          COUPON_CODE={COUPON_CODE}
          handleCopyCode={handleCopyCode}
          copied={copied}
        />
      )}

      {/* Sticky header  */}
      <div className="sticky left-0 right-0 top-0 z-[9999]">
        <header
          role="banner"
          className="h-[60px] w-full bg-white px-3 shadow-[0_1px_4px_0_rgba(0,0,0,0.08)] border-b-none lg:border-b border-gray-100 lg:shadow-none md:px-[40px] xl:px-20"
        >
          <div className="flex h-full w-full items-center justify-between gap-4">
            {/* LEFT — logo */}
            <Link
              href={HOME_PAGE}
              aria-label="Home"
              className="logo flex items-center [&_img]:!w-auto [&_img]:!h-10 sm:[&_img]:!h-10 lg:[&_img]:!h-10 3xl:[&_img]:!h-12 4xl:[&_img]:!h-16"
            >
              <DynamicImage
                src="/assets/images/imagiveLogo.svg"
                alt="Imagive Logo"
                title="Imagive Logo"
                loading="eager"
                width={180}
                height={80}
              />
            </Link>

            {/* CENTER — primary navbar (lg+ only) */}
            <div className="hidden flex-1 justify-center lg:flex">
              <Navbar isOpen={true} toggleMenu={toggleMenu} />
            </div>

            {/* RIGHT — sign-in + hamburger (mobile) / sign-in + get started (desktop) */}
            <div className="button-group flex items-center gap-2 md:gap-3 lg:gap-4 whitespace-nowrap">
              <a
                href="https://app.imagive.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost hidden md:inline-flex items-center gap-2 font-medium text-brand-ink 2xl:text-[16px] 3xl:text-[18px] 4xl:text-[20px]"
              >
                Sign in
              </a>
              <a
                href="https://app.imagive.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex md:hidden items-center text-[13px] h-9 px-3 rounded-[10px]"
              >
                Sign in
              </a>
              <button
                className="burger-menu flex lg:hidden items-center justify-center h-9 w-9 rounded-[10px] border border-black/10 text-[#292929] hover:bg-gray-100/50 transition-colors cursor-pointer"
                onClick={toggleMenu}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
                type="button"
              >
                {isMenuOpen ? (
                  <X size={20} strokeWidth={1.5} />
                ) : (
                  <Menu size={20} strokeWidth={1.5} />
                )}
              </button>
              <a
                href="https://cal.com/imagive.ai/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary group hidden lg:inline-flex items-center gap-1 4xl:text-[18px]"
              >
                <span>Get Started</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  <ChevronRight size={20} strokeWidth={1.5} />
                </span>
              </a>
            </div>
          </div>
        </header>

        {/* MOBILE PANEL — opens downward from under the bar. Animating max-height
            keeps it in flow so the page content underneath shifts down. */}
        <div
          className={`overflow-hidden rounded-b-2xl border-t border-gray-100 bg-white transition-[max-height] duration-300 ease-in-out lg:hidden ${
            isMenuOpen
              ? "max-h-[34rem] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]"
              : "max-h-0"
          }`}
        >
          <div className="px-3 py-4 md:px-[40px]">
            <Navbar isOpen={isMenuOpen} toggleMenu={toggleMenu} isMobile />
          </div>
        </div>
      </div>
    </>
  );
}
